import axios from 'axios';

import { ProjectStatsV2, StudioStatsV2, UserStatSingleV2, UserStatsV2 } from '../../entities/analytics-v2';
import { ProjectStats } from '../../entities/project.stat';
import { StandaloneProjectAnalytics } from '../../entities/standalone-analytics';
import { StandaloneProjectSummary } from '../../entities/standalone-project';
import { StudioStats } from '../../entities/studio.stat';
import { AnomalousTaskDTO, IssueDetailDTO, ProjectFlowSummary, TaskTransitionTimeline } from '../../entities/task-flow';
import { TrelloBoardStats } from '../../entities/trello-board-stats';
import { TrelloCardDetail, TrelloCardSummary } from '../../entities/trello-card';
import { auth } from '../firebase/firebase';
import { API_BASE_URL } from './index';

export const getDashboardData = async (range?: { from: string | Date; to: string | Date }) => {
  return getStudioStats(range);
};

export const getProjectStatOrCreate = async (
  projectId: string,
  dateRange: { from: string; to: string },
): Promise<{
  stat: ProjectStats;
}> => {
  try {
    const idToken = await auth.currentUser?.getIdToken();
    const response = await axios.get(
      `${API_BASE_URL}/api/dashboard/projects/${projectId}?from=${dateRange.from}&to=${dateRange.to}`,
      {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      },
    );
    return await response.data;
  } catch (error) {
    console.error('Error fetching project stats:', error);
    throw error;
  }
};

export const getTOPRiskyTasks = async (projectId: string, dateRange: { from: string | Date; to: string | Date }) => {
  try {
    const idToken = await auth.currentUser?.getIdToken();
    const params: any = {
      limit: 10,
    };
    if (dateRange) {
      params['from'] = toDateWithoutTime(dateRange.from);
      params['to'] = toDateWithoutTime(dateRange.to);
    }
    const response = await axios.get(`${API_BASE_URL}/api/dashboard/projects/${projectId}/risky-tasks`, {
      params,
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
    });
    return await response.data;
  } catch (error) {
    console.error('Error fetching project stats:', error);
    throw error;
  }
};

export const getProjectsStats = async ({
  projectIds,
  range,
}: {
  projectIds: string[];
  range?: { from: string | Date; to: string | Date };
}) => {
  try {
    const idToken = await auth.currentUser?.getIdToken();
    if (!projectIds || projectIds.length === 0) {
      return [];
    }
    const params: any = {
      projectIds,
    };
    if (range) {
      params['from'] = toDateWithoutTime(range.from);
      params['to'] = toDateWithoutTime(range.to);
    }
    const response = await axios.get(`${API_BASE_URL}/api/dashboard/projects`, {
      params,
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
    });
    return await response.data;
  } catch (error) {
    console.error('Error fetching project stats:', error);
    throw error;
  }
};

export const getStudioStats = async (dateRange?: { from: string | Date; to: string | Date }) => {
  try {
    const idToken = await auth.currentUser?.getIdToken();
    const params: any = {};
    if (dateRange) {
      params['from'] = toDateWithoutTime(dateRange.from);
      params['to'] = toDateWithoutTime(dateRange.to);
    }
    const response = await axios.get<{ stat: StudioStats }>(`${API_BASE_URL}/api/dashboard`, {
      params,
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
    });
    return await response.data;
  } catch (error) {
    console.error('Error fetching project stats:', error);
    throw error;
  }
};

export const getUsersStats = async (projectId: string, range?: { from: string | Date; to: string | Date }) => {
  try {
    const idToken = await auth.currentUser?.getIdToken();
    const params: any = {};
    if (range) {
      params['from'] = toDateWithoutTime(range.from);
      params['to'] = toDateWithoutTime(range.to);
    }
    const response = await axios.get(`${API_BASE_URL}/api/dashboard/users/${projectId}`, {
      params,
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
    });
    return await response.data;
  } catch (error) {
    console.error('Error fetching project stats:', error);
    throw error;
  }
};

export const getUserStats = async (
  projectId: string,
  userId: string,
  range?: { from: string | Date; to: string | Date },
) => {
  try {
    const idToken = await auth.currentUser?.getIdToken();
    const params: any = {};
    if (range) {
      params['from'] = toDateWithoutTime(range.from);
      params['to'] = toDateWithoutTime(range.to);
    }
    const response = await axios.get(`${API_BASE_URL}/api/dashboard/users/${projectId}/${userId}`, {
      params,
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
    });
    return await response.data;
  } catch (error) {
    console.error('Error fetching project stats:', error);
    throw error;
  }
};

export const getStudioStatsV2 = async (dateRange?: { from: string | Date; to: string | Date }) => {
  try {
    const idToken = await auth.currentUser?.getIdToken();
    const params: any = {};
    if (dateRange) {
      params['from'] = toDateWithoutTime(dateRange.from);
      params['to'] = toDateWithoutTime(dateRange.to);
    }
    const response = await axios.get<{ stat: StudioStatsV2 }>(`${API_BASE_URL}/api/dashboard/v2`, {
      params,
      headers: { Authorization: `Bearer ${idToken}` },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching studio stats v2:', error);
    throw error;
  }
};

export const getProjectStatsV2 = async (
  projectId: string,
  dateRange: { from: string; to: string },
): Promise<{ stat: ProjectStatsV2 }> => {
  try {
    const idToken = await auth.currentUser?.getIdToken();
    const response = await axios.get<{ stat: ProjectStatsV2 }>(
      `${API_BASE_URL}/api/dashboard/v2/projects/${projectId}?from=${dateRange.from}&to=${dateRange.to}`,
      { headers: { Authorization: `Bearer ${idToken}` } },
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching project stats v2:', error);
    throw error;
  }
};

export const getUsersStatsV2 = async (projectId: string, range?: { from: string | Date; to: string | Date }) => {
  try {
    const idToken = await auth.currentUser?.getIdToken();
    const params: any = {};
    if (range) {
      params['from'] = toDateWithoutTime(range.from);
      params['to'] = toDateWithoutTime(range.to);
    }
    const response = await axios.get<{ stat: UserStatsV2[] }>(`${API_BASE_URL}/api/dashboard/v2/users/${projectId}`, {
      params,
      headers: { Authorization: `Bearer ${idToken}` },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching users stats v2:', error);
    throw error;
  }
};

export const getUserStatsV2 = async (
  projectId: string,
  userId: string,
  range?: { from: string | Date; to: string | Date },
) => {
  try {
    const idToken = await auth.currentUser?.getIdToken();
    const params: any = {};
    if (range) {
      params['from'] = toDateWithoutTime(range.from);
      params['to'] = toDateWithoutTime(range.to);
    }
    const response = await axios.get<{ stat: UserStatSingleV2 }>(
      `${API_BASE_URL}/api/dashboard/v2/users/${projectId}/${userId}`,
      { params, headers: { Authorization: `Bearer ${idToken}` } },
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching user stats v2:', error);
    throw error;
  }
};

export const getTrelloBoardStats = async (
  boardId: string,
  dateRange: { from: string; to: string },
): Promise<{ stat: TrelloBoardStats }> => {
  const idToken = await auth.currentUser?.getIdToken();
  const response = await axios.get<{ stat: TrelloBoardStats }>(
    `${API_BASE_URL}/api/dashboard/trello/${boardId}?from=${dateRange.from}&to=${dateRange.to}`,
    { headers: { Authorization: `Bearer ${idToken}` } },
  );
  return response.data;
};

export const getTrelloBoardCards = async (boardId: string): Promise<{ cards: TrelloCardSummary[] }> => {
  const idToken = await auth.currentUser?.getIdToken();
  const response = await axios.get<{ cards: TrelloCardSummary[] }>(
    `${API_BASE_URL}/api/dashboard/trello/${boardId}/cards`,
    { headers: { Authorization: `Bearer ${idToken}` } },
  );
  return response.data;
};

export const getTrelloCardDetail = async (cardId: string): Promise<{ card: TrelloCardDetail }> => {
  const idToken = await auth.currentUser?.getIdToken();
  const response = await axios.get<{ card: TrelloCardDetail }>(`${API_BASE_URL}/api/dashboard/trello/cards/${cardId}`, {
    headers: { Authorization: `Bearer ${idToken}` },
  });
  return response.data;
};

export const getStandaloneProjectSummary = async (projectId: string): Promise<{ stat: StandaloneProjectSummary }> => {
  const idToken = await auth.currentUser?.getIdToken();
  const response = await axios.get<{ stat: StandaloneProjectSummary }>(
    `${API_BASE_URL}/api/dashboard/standalone/${projectId}`,
    { headers: { Authorization: `Bearer ${idToken}` } },
  );
  return response.data;
};

// `dateRange` is optional — the BE only applies `from`/`to` together (passing just one is treated
// as neither) and otherwise defaults to the last 30 days vs. the prior 30 (MVPB-110).
export const getStandaloneProjectAnalytics = async (
  projectId: string,
  dateRange?: { from: string; to: string },
): Promise<{ stat: StandaloneProjectAnalytics }> => {
  const idToken = await auth.currentUser?.getIdToken();
  const response = await axios.get<{ stat: StandaloneProjectAnalytics }>(
    `${API_BASE_URL}/api/dashboard/standalone/${projectId}/flow-summary`,
    {
      headers: { Authorization: `Bearer ${idToken}` },
      params: dateRange ? { from: dateRange.from, to: dateRange.to } : undefined,
    },
  );
  return response.data;
};

export const getProjectFlowSummary = async (
  projectId: string,
  dateRange: { from: string; to: string },
): Promise<{ stat: ProjectFlowSummary }> => {
  const idToken = await auth.currentUser?.getIdToken();
  const response = await axios.get<{ stat: ProjectFlowSummary }>(
    `${API_BASE_URL}/api/dashboard/projects/${projectId}/flow-summary?from=${dateRange.from}&to=${dateRange.to}`,
    { headers: { Authorization: `Bearer ${idToken}` } },
  );
  return response.data;
};

export const getTaskTimeline = async (issueId: string): Promise<{ timeline: TaskTransitionTimeline }> => {
  const idToken = await auth.currentUser?.getIdToken();
  const response = await axios.get<{ timeline: TaskTransitionTimeline }>(
    `${API_BASE_URL}/api/dashboard/tasks/${issueId}/timeline`,
    { headers: { Authorization: `Bearer ${idToken}` } },
  );
  return response.data;
};

export const getJiraTaskDetail = async (issueId: string): Promise<{ task: IssueDetailDTO }> => {
  const idToken = await auth.currentUser?.getIdToken();
  const response = await axios.get<{ task: IssueDetailDTO }>(`${API_BASE_URL}/api/dashboard/tasks/${issueId}`, {
    headers: { Authorization: `Bearer ${idToken}` },
  });
  return response.data;
};

export const getProjectAnomalies = async (
  projectId: string,
  dateRange: { from: string; to: string },
  limit?: number,
): Promise<{ tasks: AnomalousTaskDTO[] }> => {
  const idToken = await auth.currentUser?.getIdToken();
  const response = await axios.get<{ tasks: AnomalousTaskDTO[] }>(
    `${API_BASE_URL}/api/dashboard/projects/${projectId}/anomalies`,
    {
      params: { from: dateRange.from, to: dateRange.to, limit },
      headers: { Authorization: `Bearer ${idToken}` },
    },
  );
  return response.data;
};

const toDateWithoutTime = (date: string | Date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};
